import cs from 'classnames';
import { Button, Input, MagellanPopup } from 'components/atoms';
import React, { CSSProperties, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  PiDownload,
  PiFloppyDisk,
  PiFolder,
  PiImageSquare,
  PiImageSquareLight,
  PiMagnifyingGlassMinus,
  PiMagnifyingGlassPlus,
  PiPencil,
  PiPlus,
  PiTrash,
  PiX,
} from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';

import { getIn, useFormikContext } from 'formik';

import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { ILoadingProp } from 'types/components/components.interface';
import { IImageSize, ISelectOption } from 'types/components/inputs/input.interface';
import { getCroppedImg, getImageSize } from 'utils/components/inputs/image';

import { FormStatusWithImageDrafts, ImageCropDraft } from 'types/components/forms/imageDrafts';
import { throttle } from 'utils/components/inputs/throttle';
import './ImageInputForm.scss';

export interface ImageInputFormFieldProps<TForm extends Record<string, any>> extends ILoadingProp {
  nameCropped: string;
  nameOriginal: string;
  draftKey?: string;
  fullResolutionUrl?: string | null;
  previewUrl?: string | null;
  alt?: string;
  editable?: boolean;
  title?: string;
  onDelete?: (actions: PopupActions) => void;
  onCancel?: (actions: PopupActions) => void;
  onClose?: (
    event?: SyntheticEvent<Element, Event> | KeyboardEvent | TouchEvent | MouseEvent | undefined,
  ) => void;
  disable?: boolean;
  imageSize?: IImageSize;
  imageSizeSelectOptions?: ISelectOption<number>[];
  isError?: boolean;
  imagePlaceholder?: React.ReactNode;
  accept?: string;
}

const DEFAULT_DRAFT: ImageCropDraft = {
  crop: { x: 0, y: 0 },
  zoom: 10,
  areaPixels: null,
  updatedAt: Date.now(),
};

const ImageInputFormField = <TForm extends Record<string, any>>({
  nameCropped,
  nameOriginal,
  draftKey,
  fullResolutionUrl,
  previewUrl,
  alt,
  editable = false,
  title,
  loading,
  onDelete,
  onClose,
  onCancel,
  disable,
  imageSize = getImageSize(500),
  imageSizeSelectOptions,
  isError,
  accept = 'image/jpg, image/jpeg',
  imagePlaceholder = <PiImageSquareLight className="placeholder" />,
}: ImageInputFormFieldProps<TForm>) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<PopupActions>(null);

  const restoringRef = useRef(false);
  const pendingRestoreRef = useRef<ImageCropDraft | null>(null);

  const formik = useFormikContext<TForm>();

  const key = draftKey ?? nameCropped;

  const croppedValue = (getIn(formik.values, nameCropped) as File | null | undefined) ?? null;
  const originalValue = (getIn(formik.values, nameOriginal) as File | null | undefined) ?? null;
  const isBlobLike = (value: unknown): value is Blob => value instanceof Blob;

  const lastUrlRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const candidate = previewUrl ?? fullResolutionUrl;
    if (candidate) lastUrlRef.current = candidate;
  }, [previewUrl, fullResolutionUrl]);
  const stableUrl = previewUrl ?? fullResolutionUrl ?? lastUrlRef.current;

  const revokeSafe = (url: string | null) => {
    if (!url) return;
    try {
      URL.revokeObjectURL(url);
    } catch {}
  };

  const [croppedUrl, setCroppedUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!croppedValue || !isBlobLike(croppedValue)) {
      setCroppedUrl((prev) => {
        revokeSafe(prev);
        return null;
      });
      return;
    }
    const url = URL.createObjectURL(croppedValue);
    setCroppedUrl((prev) => {
      revokeSafe(prev);
      return url;
    });
    return () => revokeSafe(url);
  }, [croppedValue]);

  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!originalValue || !isBlobLike(originalValue)) {
      setOriginalUrl((prev) => {
        revokeSafe(prev);
        return null;
      });
      return;
    }
    const url = URL.createObjectURL(originalValue);
    setOriginalUrl((prev) => {
      revokeSafe(prev);
      return url;
    });
    return () => revokeSafe(url);
  }, [originalValue]);

  const [newImage, setNewImage] = useState<File | null>(null);
  const [newImageUrl, setNewImageUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!newImage) {
      setNewImageUrl((prev) => {
        revokeSafe(prev);
        return null;
      });
      return;
    }
    const url = URL.createObjectURL(newImage);
    setNewImageUrl((prev) => {
      revokeSafe(prev);
      return url;
    });
    return () => revokeSafe(url);
  }, [newImage]);

  const status = (formik.status ?? {}) as FormStatusWithImageDrafts;
  const statusRef = useRef<FormStatusWithImageDrafts>(status);

  useEffect(() => {
    statusRef.current = (formik.status ?? {}) as FormStatusWithImageDrafts;
  }, [formik.status]);

  const getDraft = (): ImageCropDraft => statusRef.current.imageDrafts?.[key] ?? DEFAULT_DRAFT;

  const upsertDraft = (partial: Partial<ImageCropDraft>) => {
    const prev = statusRef.current ?? {};
    const prevDraft = prev.imageDrafts?.[key] ?? DEFAULT_DRAFT;

    const nextStatus: FormStatusWithImageDrafts = {
      ...prev,
      imageDrafts: {
        ...(prev.imageDrafts ?? {}),
        [key]: {
          ...prevDraft,
          ...partial,
          updatedAt: Date.now(),
        },
      },
    };

    formik.setStatus(nextStatus);
    statusRef.current = nextStatus;
  };

  const setDraftThrottled = useMemo(
    () => throttle((partial: Partial<ImageCropDraft>) => upsertDraft(partial), 80),

    [key],
  );

  const zoomSmoothness = 10;
  const maxZoom = 3 * zoomSmoothness;
  const initialCropValues = { x: 0, y: 0 };

  const initialDraft = getDraft();

  const [crop, setCrop] = useState(initialDraft.crop ?? initialCropValues);
  const [zoom, setZoom] = useState(initialDraft.zoom ?? zoomSmoothness);
  const [areaPixels, setAreaPixels] = useState<Area | null>(initialDraft.areaPixels ?? null);

  const [newImageSize, setNewImageSize] = useState<IImageSize>(imageSize);
  const [imageError, setImageError] = useState(false);

  const triggerUrl = croppedUrl ?? stableUrl;

  const cropperSourceUrl = newImageUrl ?? originalUrl ?? fullResolutionUrl ?? stableUrl;

  const canEditCrop = Boolean(newImageUrl ?? originalUrl);

  useEffect(() => {
    setImageError(false);
  }, [triggerUrl, cropperSourceUrl]);

  const onCropChange = (location: { x: number; y: number }) => {
    if (!canEditCrop) return;
    setCrop(location);
    if (!restoringRef.current) setDraftThrottled({ crop: location });
  };

  const onZoomChange = (z: number) => {
    if (!canEditCrop) return;
    const zSmooth = z * zoomSmoothness;
    setZoom(zSmooth);
    if (!restoringRef.current) setDraftThrottled({ zoom: zSmooth });
  };

  const onCropComplete = (_area: Area, pixels: Area) => {
    setAreaPixels(pixels);
    if (!restoringRef.current) setDraftThrottled({ areaPixels: pixels });
  };

  const openFiles = () => inputRef.current?.click();

  const cancel = () => {
    popupRef.current && onCancel && onCancel(popupRef.current);
    popupRef.current?.close();
    setNewImage(null);
  };

  const onDownloadImage = () => toast.success(t('toast-message.image-downloaded-successfully'));

  const writeDraftNow = (partial: Partial<ImageCropDraft>) => {
    upsertDraft(partial);
  };

  const applyCrop = async () => {
    writeDraftNow({ crop, zoom, areaPixels });

    const sourceFile = newImage ?? originalValue;
    if (!sourceFile) return;

    const pixels =
      areaPixels ??
      ({
        x: 0,
        y: 0,
        width: newImageSize.width,
        height: newImageSize.height,
      } as Area);

    const croppedFile = await getCroppedImg(
      sourceFile,
      pixels,
      newImageSize.height,
      newImageSize.width,
    );

    formik.setFieldValue(nameCropped, croppedFile, true);

    if (newImage) {
      formik.setFieldValue(nameOriginal, newImage, false);
    }

    setNewImage(null);
  };

  const onDeleteClick = () => {
    if (newImage) {
      setNewImage(null);
      return;
    }
    if (popupRef.current && onDelete) onDelete(popupRef.current);
  };

  const triggerClassNames = cs(['image-input-trigger'], {
    editable: !disable && editable,
    loading: loading,
    disable: disable,
    error: !disable && isError,
    downloadable: !disable && !editable && !!triggerUrl,
  });

  return (
    <Popup
      ref={popupRef}
      contentStyle={
        {
          '--width-multiple': imageSize.width / imageSize.height,
        } as CSSProperties
      }
      trigger={
        <div className={triggerClassNames}>
          {triggerUrl && !imageError ? (
            !editable && (fullResolutionUrl || triggerUrl) ? (
              <a
                href={fullResolutionUrl ?? triggerUrl}
                className="download-href"
                onClick={onDownloadImage}
              >
                <img
                  src={triggerUrl}
                  alt={alt ?? triggerUrl}
                  loading="lazy"
                  onError={() => setImageError(true)}
                />
              </a>
            ) : (
              <img
                src={triggerUrl}
                alt={alt ?? triggerUrl}
                loading="lazy"
                onError={() => setImageError(true)}
              />
            )
          ) : (
            imagePlaceholder
          )}

          <div className="hover-overlay">
            {!disable && editable ? (
              triggerUrl ? (
                <PiPencil />
              ) : (
                <PiPlus className="new" />
              )
            ) : (
              triggerUrl && <PiDownload />
            )}
          </div>
        </div>
      }
      onOpen={() => {
        const d = getDraft();

        console.log('[POPUP OPEN] statusRef.current:', statusRef.current);
        console.log('[POPUP OPEN] draft:', statusRef.current.imageDrafts?.[key]);

        pendingRestoreRef.current = d;
        restoringRef.current = true;

        if (!cropperSourceUrl && !newImage) openFiles();
      }}
      disabled={!editable || disable}
      onClose={onClose}
      modal
      nested
    >
      <MagellanPopup className="image-popup-form">
        <MagellanPopup.Header>
          <PiImageSquare />
          {title ?? t('image-input-popup.header')}
        </MagellanPopup.Header>

        {(cropperSourceUrl || newImage) && imageSizeSelectOptions && (
          <div className="size-select">
            <Input.Select
              label={t('image-input-popup.image-size')}
              value={{
                label: `${newImageSize.height} px`,
                value: newImageSize,
              }}
              options={imageSizeSelectOptions}
              onChange={(option) => setNewImageSize(getImageSize(option.value as number))}
            />
          </div>
        )}

        <div className={`crop-wrapper${imageSize.width === imageSize.height ? ' square' : ''}`}>
          {!cropperSourceUrl ? (
            imagePlaceholder
          ) : (
            <Cropper
              image={cropperSourceUrl}
              crop={crop}
              zoom={zoom / zoomSmoothness}
              aspect={imageSize.width / imageSize.height}
              onCropChange={onCropChange}
              onCropComplete={onCropComplete}
              onZoomChange={onZoomChange}
              onMediaLoaded={() => {
                if (!restoringRef.current) return;

                const d = pendingRestoreRef.current ?? DEFAULT_DRAFT;

                setCrop(d.crop ?? initialCropValues);
                setZoom(d.zoom ?? zoomSmoothness);
                setAreaPixels(d.areaPixels ?? null);

                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    restoringRef.current = false;
                    pendingRestoreRef.current = null;
                  });
                });
              }}
              objectFit="cover"
            />
          )}
        </div>

        <div className="zoom-range">
          <PiMagnifyingGlassMinus />
          <input
            type="range"
            value={zoom}
            onChange={(e) => {
              if (!canEditCrop) return;
              const zSmooth = parseInt(e.target.value);
              setZoom(zSmooth);
              if (!restoringRef.current) setDraftThrottled({ zoom: zSmooth });
            }}
            min={zoomSmoothness}
            max={maxZoom}
            disabled={!canEditCrop}
          />
          <PiMagnifyingGlassPlus />
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="invisible-file-input"
          onChange={(e) => e.target.files?.length && setNewImage(e.target.files[0])}
        />

        <div className="buttons">
          <div className="flex gap-2">
            {onDelete && (
              <Button variant={BUTTON_VARIANT.remove} onlyIcon isClear onClick={onDeleteClick}>
                <Button.Icon icon={<PiTrash />} />
              </Button>
            )}
            <Button onlyIcon isClear onClick={openFiles}>
              <Button.Icon icon={<PiFolder />} />
            </Button>
          </div>

          <div className="flex gap-2">
            <Button onlyIcon isClear onClick={cancel}>
              <Button.Icon icon={<PiX />} />
            </Button>

            <Button
              variant={BUTTON_VARIANT.submit}
              disabled={!(newImage || originalValue)}
              onClick={async () => {
                await applyCrop();
                popupRef.current?.close();
              }}
            >
              <Button.Text text={t('image-input-popup.save')} />
              <Button.Icon icon={<PiFloppyDisk />} />
            </Button>
          </div>
        </div>
      </MagellanPopup>
    </Popup>
  );
};

export default ImageInputFormField;
